import { Badge } from "antd";
import Dropdown from "components/dropdown";
import { API_NOTIFICATIONS_FROM_NAVBAR } from "api/Notification-management";
import { useMessages } from "config/MessageProvider";
import { useRef, useState } from "react";
import { IoMdNotificationsOutline } from "react-icons/io";
import { CONSTANTS } from "config/constant";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useInfiniteQuery } from "@tanstack/react-query";
import useInfiniteScroll from "beautiful-react-hooks/useInfiniteScroll";

const fetchNotifications = async ({ pageParam = 1 }) => {
  const params = {
    page: pageParam,
    limit: 8, // change this limit if you want to show more notifications
  };
  const response = await API_NOTIFICATIONS_FROM_NAVBAR(params);
  return response;
};

function Notifications() {
  const scrollingContainerRef = useRef();
  const onInfiniteScroll = useInfiniteScroll(scrollingContainerRef);

  const [opened, setOpened] = useState(false);
  const { notificationsCount, setNotificationsCount } = useMessages();
  const language = useSelector((state) => state?.user?.language);
  const navigate = useNavigate();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["notifications"],
      queryFn: fetchNotifications,
      getNextPageParam: (lastPage) => {
        if (lastPage?.page < lastPage?.total_pages) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    });

  const notifications = data?.pages.flatMap((page) => page?.data || []) || [];

  onInfiniteScroll(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  return (
    <Dropdown
      button={
        <p
          className="cursor-pointer"
          onClick={() => {
            setOpened(true);
            setNotificationsCount([]);
          }}
        >
          {notificationsCount?.length > 0 && opened === false ? (
            <Badge
              count={notificationsCount?.length}
              offset={[-10, 10]}
              className="notification-badge absolute right-0 top-0 mt-0"
            >
              <IoMdNotificationsOutline className="h-4 w-4 text-gray-600 dark:text-white" />
            </Badge>
          ) : (
            <IoMdNotificationsOutline className="h-4 w-4 text-gray-600 dark:text-white" />
          )}
        </p>
      }
      animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
      children={
        <div
          ref={scrollingContainerRef}
          className="flex max-h-[600px] min-h-[600px]  w-[360px] flex-col gap-3 overflow-y-scroll rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none sm:w-[460px]"
        >
          {notifications?.length > 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-base font-bold text-navy-700 dark:text-white">
                Notification
              </p>
            </div>
          ) : (
            <p className="text-center">No Notifications</p>
          )}

          {notifications?.map((not) => (
            <div key={not._id}>
              <button
                className="flex h-full max-h-[20rem] w-full items-center overflow-y-hidden"
                onClick={() => {
                  switch (not.notification_content.navigationId) {
                    case "laundry-man":
                      navigate(
                        `/admin/laundryman-management/laundryman-tables`
                      );
                      break;
                    default:
                      navigate(
                        `/admin/order-management/${not?.notification_content?.order_id}`
                      );
                      break;
                  }
                }}
              >
                <div className="flex h-full w-[85px] items-center justify-center rounded-xl bg-gradient-to-b py-4 text-2xl text-white">
                  <img
                    src={
                      CONSTANTS?.IMG_DIRECTORY +
                      not?.send_from_info?.profile_image?.[0]?.file_url
                    }
                    className="size-[4rem] object-cover"
                    style={{ borderRadius: "50%" }}
                    alt={not.send_from_info.first_name}
                  />
                </div>
                <div className="ml-2 flex h-full w-full flex-col justify-center rounded-lg px-1 text-sm">
                  <div className="flex items-center justify-between">
                    <p className="mb-1 text-left text-base font-bold text-gray-900 dark:text-white">
                      {not?.send_from_info?.first_name +
                        " " +
                        not?.send_from_info?.last_name}
                    </p>
                    <p className="font-base text-left text-[11px] text-gray-900 dark:text-white">
                      {moment
                        .unix(not?.created_at)
                        .format("DD MMMM YYYY, HH:mm:ss")}
                    </p>
                  </div>
                  <p className="font-base text-left text-xs text-gray-900 dark:text-white">
                    {not?.notification_content?.message}
                  </p>
                </div>
              </button>
              <hr />
            </div>
          ))}
        </div>
      }
      classNames={`py-2 top-4 ${
        language === "ar"
          ? "md:-right-[440px] left-0"
          : "md:-left-[440px] -left-[230px]"
      }  w-max`}
      onClose={() => setOpened(false)}
    />
  );
}

export default Notifications;
